<script setup lang="ts">
import { EmptyState, TraceIdAlert, formatDateTime } from '@yc/ui-kit'
import { onMounted, reactive, ref } from 'vue'

import {
  KIND_LABELS,
  STATUS_LABELS,
  STATUS_TAG,
  canAct,
  listReceipts,
  returnReceipt,
  submitReceipt,
  type ReceiptAction,
  type ReceiptKind,
  type ReceiptRow,
  type ReceiptStatus,
} from '../api/receipts'

const rows = ref<ReceiptRow[]>([])
const total = ref(0)
const loading = ref(false)
const error = ref<unknown>(null)
/** 提交 / 退回失败的错误：409 的原因与 trace_id 都要露出来 */
const refError = ref<unknown>(null)
const notice = ref('')

const query = reactive({
  keyword: '',
  status: '' as ReceiptStatus | '',
  kind: '' as ReceiptKind | '',
  page: 1,
  size: 20,
})

async function load(noticeText = ''): Promise<void> {
  loading.value = true
  error.value = null
  try {
    const res = await listReceipts({
      page: query.page,
      size: query.size,
      keyword: query.keyword.trim() || undefined,
      status: query.status || undefined,
      kind: query.kind || undefined,
    })
    rows.value = res.list
    total.value = res.total
    notice.value = noticeText
  } catch (e) {
    error.value = e
    rows.value = []
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  void load()
})

function search(): void {
  query.page = 1
  void load()
}

function page(p: number): void {
  query.page = p
  void load()
}

async function act(kind: ReceiptAction, row: ReceiptRow): Promise<void> {
  notice.value = ''
  refError.value = null
  try {
    if (kind === 'submit') await submitReceipt(row.id)
    else await returnReceipt(row.id)
    await load(kind === 'submit' ? `${row.receipt_no} 已提交` : `${row.receipt_no} 已退回`)
  } catch (e) {
    refError.value = e
  }
}
</script>

<template>
  <div class="yc-receipt">
    <header class="yc-receipt__bar">
      <el-input
        v-model="query.keyword"
        class="yc-receipt__keyword"
        placeholder="回单号 / 运单号"
        clearable
        @keyup.enter="search"
        @clear="search"
      />
      <el-select
        v-model="query.status"
        class="yc-receipt__ref"
        placeholder="全部状态"
        clearable
        @change="search"
      >
        <el-option
          v-for="(label, value) in STATUS_LABELS"
          :key="value"
          :label="label"
          :value="value"
        />
      </el-select>
      <el-select
        v-model="query.kind"
        class="yc-receipt__ref"
        placeholder="全部类型"
        clearable
        @change="search"
      >
        <el-option
          v-for="(label, value) in KIND_LABELS"
          :key="value"
          :label="label"
          :value="value"
        />
      </el-select>
      <el-button @click="search">查询</el-button>
      <el-button @click="load()">刷新</el-button>
      <span v-if="notice" class="yc-receipt__notice">{{ notice }}</span>
    </header>

    <div v-if="error || refError" class="yc-receipt__error">
      <TraceIdAlert :error="error ?? refError" />
    </div>

    <el-card v-loading="loading" class="yc-receipt__card">
      <el-table v-if="rows.length" :data="rows" row-key="id">
        <el-table-column prop="receipt_no" label="回单号" min-width="150" fixed="left" />
        <el-table-column label="所属运单" min-width="150" show-overflow-tooltip>
          <template #default="{ row }">{{ row.shipment_doc_no || '—' }}</template>
        </el-table-column>
        <el-table-column label="类型" width="110">
          <template #default="{ row }">
            <el-tag size="small" type="info">{{ KIND_LABELS[row.kind as ReceiptKind] ?? row.kind }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="100">
          <template #default="{ row }">
            <el-tag :type="STATUS_TAG[row.status as ReceiptStatus] ?? 'info'" size="small">
              {{ STATUS_LABELS[row.status as ReceiptStatus] ?? row.status }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="提交时间" min-width="150">
          <template #default="{ row }">{{ formatDateTime(row.submitted_at) }}</template>
        </el-table-column>
        <el-table-column label="退回时间" min-width="150">
          <template #default="{ row }">{{ formatDateTime(row.returned_at) }}</template>
        </el-table-column>
        <el-table-column prop="remark" label="备注" min-width="150" show-overflow-tooltip>
          <template #default="{ row }">{{ row.remark || '—' }}</template>
        </el-table-column>
        <el-table-column label="操作" width="150" fixed="right">
          <template #default="{ row }">
            <el-popconfirm
              v-if="canAct(row.status as ReceiptStatus, 'submit')"
              title="确认提交该回单？提交后可再次退回重传。"
              width="300"
              @confirm="act('submit', row)"
            >
              <template #reference>
                <el-button v-perm="'pod:receipt:submit'" link type="primary">提交</el-button>
              </template>
            </el-popconfirm>
            <el-popconfirm
              v-if="canAct(row.status as ReceiptStatus, 'return')"
              title="确认退回该回单？退回后需重新提交。"
              width="300"
              @confirm="act('return', row)"
            >
              <template #reference>
                <el-button v-perm="'pod:receipt:return'" link type="danger">退回</el-button>
              </template>
            </el-popconfirm>
          </template>
        </el-table-column>
      </el-table>

      <EmptyState
        v-else-if="!loading"
        title="还没有回单"
        description="运单签满后系统会自动生成一张电子回单，在这里提交给货主或承运商归档。"
      />
    </el-card>

    <el-pagination
      v-if="total > query.size"
      class="yc-receipt__page"
      layout="total, prev, pager, next"
      :total="total"
      :page-size="query.size"
      :current-page="query.page"
      @current-change="page"
    />
  </div>
</template>

<style scoped lang="scss">
.yc-receipt {
  &__bar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--yc-space-2);
    margin-bottom: var(--yc-space-4);
  }

  &__keyword {
    width: 220px;
  }

  &__ref {
    width: 150px;
  }

  &__notice {
    color: var(--yc-text-secondary);
    font-size: var(--yc-font-sm);
  }

  &__error {
    margin-bottom: var(--yc-space-3);
    font-size: var(--yc-font-sm);
  }

  &__page {
    margin-top: var(--yc-space-4);
    justify-content: flex-end;
  }
}
</style>
